import Vue from 'vue';
import Moment from 'moment-timezone';
import toastr from 'toastr';
import { cloneDeep } from 'lodash';
import { calert } from '@/utils/main';
import { i18n } from '@/plugins/i18n';
import { SEND_MESSAGE } from '@/store/modules/socket/action-types';
import SocketMessages from '@/utils/classes/SocketMessages';
import { Action } from '@/models/Action';
import { Problem } from '@/models/Problem';
import * as mutationsTypes from './mutation-types';
import * as actionsTypes from './action-types';

export default {

  /** Fetch a specific problem along with all related causes and actions */
  async [actionsTypes.FETCH_PROBLEM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/problems/get.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data.problems instanceof Array) {
          data.problems.forEach((problem) => {
            store.commit(mutationsTypes.SET_PROBLEM, problem);
          });
          return data;
        }
        if (data instanceof Array) {
          data.forEach((problem) => {
            store.commit(mutationsTypes.SET_PROBLEM, problem);
          });
          return data;
        }
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          return data;
        }
        throw data;
      });
  },

  /** Fetch a specific problem through the new api along with all related causes and actions */
  async [actionsTypes.FETCH_SINGLE_PROBLEM](store, payload) {
    const url = `/server/problems/${payload.id}/${payload.includeData ? 'true' : 'false'}`;
    return Vue.axios({
      method: 'get',
      url,
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          return data;
        }
        throw data;
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          calert(i18n.t('message.PB_NO_ACCESS'), [{
            title: i18n.t('message.OK'),
          }]);
        }
      });
  },

  /** Fetch problem with filters */
  async [actionsTypes.FETCH_FILTERED_PROBLEM](store, payload) {
    return Vue.axios({
      method: 'get',
      url: '/server/problems/filter',
      params: payload,
    })
      .then(({ data }) => {
        if (data instanceof Array) {
          store.commit(mutationsTypes.SET_PROBLEMS, { payload, data });
          data.forEach((problem) => {
            store.commit(mutationsTypes.SET_PROBLEM, problem);
            if (problem.actions && problem.actions.length) {
              problem.actions.forEach((action) => {
                store.commit(mutationsTypes.SET_ACTION, action);
              });
            }
          });
          return data;
        }
        return [];
      });
  },

  /** Fetch a specific action */
  async [actionsTypes.FETCH_SINGLE_ACTION](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/actions/getCreationData.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object && data.id) {
          store.commit(mutationsTypes.SET_ACTION, data);
          return data;
        }
        throw data;
      });
  },

  /** Fetch actions with filters */
  async [actionsTypes.FETCH_FILTERED_ACTION](store, payload) {
    return Vue.axios({
      method: 'get',
      url: '/server/actions/filter',
      params: payload,
    })
      .then(({ data }) => {
        if (data instanceof Array) {
          store.commit(mutationsTypes.SET_ACTIONS, { payload, data });
          data.forEach((action) => {
            store.commit(mutationsTypes.SET_ACTION, action);
          });
          return data;
        }
        return [];
      });
  },

  /** Fetch a specific problem along with all related causes and actions */
  async [actionsTypes.ADD_PROBLEM](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems',
      data: payload,
    };
    // ===================== Offline =====================
    // Problems can be created while offlineMode is on by storing the problems using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Get new id
        const id = Math.min(...Object.values(store.state.problems).map(pb => pb.id), 0) - 1;
        // Create a new empty problem
        const problem = new Problem({ ...payload, id }, store.rootState.currentUser);
        store.commit(mutationsTypes.SET_PROBLEM, problem);
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: { ...payload, id } });
        resolve(problem);
      });
    }

    // ===================== Online =====================
    store.commit(mutationsTypes.INCREMENT_ADD_PROBLEM_CALLS);
    // Check numberof problems isn't excessive
    const secondsAgo = Moment().subtract(15, 's');
    let nCalls = 0;
    for (let i = store.state.latestProblemCalls.length; i >= 0; i--) {
      if (secondsAgo.isBefore(store.state.latestProblemCalls[i])) {
        nCalls++;
      } else {
        break;
      }
    }
    // If too big, interrupt and prompts a modal
    if (nCalls > 5) {
      return new Promise((resolve, reject) => {
        store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, true);
        store.commit(mutationsTypes.RESUME_PROBLEM_CREATION, false);
        calert(i18n.t('message.INFINITE_PB_BUG'), [
          {
            title: i18n.t('message.YES'),
            action: () => {
              store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
              store.commit(mutationsTypes.RESUME_PROBLEM_CREATION, true);
              store.dispatch(actionsTypes.ADD_PROBLEM, payload).then((res) => {
                resolve(res);
              });
            },
          },
          {
            title: i18n.t('message.NO'),
            action: () => {
              store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
              store.dispatch(actionsTypes.SEND_REPORT, { msg: store.state.creationLogBuffer });
              reject(new Error({ interrupted: true }));
            },
          },
        ], false, '', [], () => {
          store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
          store.dispatch(actionsTypes.SEND_REPORT, { msg: store.state.creationLogBuffer });
          reject(new Error({ interrupted: true }));
        });
      });
    }

    if (store.state.interruptProblemCreation) {
      // Problem creation was interrupted
      if (!store.state.problemInterruptionModalDisplayed) {
        return new Promise((resolve, reject) => {
          store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, true);
          // Prompts the user to resume problem creation
          calert(i18n.t('message.INFINITE_PB_BUG_RESUME'), [
            {
              title: i18n.t('message.YES'),
              action: () => {
                store.commit(mutationsTypes.RESUME_PROBLEM_CREATION, true);
                store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
                store.dispatch(actionsTypes.ADD_PROBLEM, payload).then((res) => {
                  resolve(res);
                });
              },
            },
            {
              title: i18n.t('message.NO'),
              action: () => {
                store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
                reject(new Error({ interrupted: true }));
              },
            },
          ], false, '', [], () => {
            store.commit(mutationsTypes.PB_INTERRUPTION_MODAL_DISPLAYED, false);
            reject(new Error({ interrupted: true }));
          });
        });
      }
      return { interrupted: true };
    }
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          return data;
        }
        throw data;
      })
      .catch(({ response }) => {
        if (response && response.status === 401) {
          calert(i18n.t('message.PB_NO_ACCESS'), [{
            title: i18n.t('message.OK'),
          }]);
        } else {
          calert(i18n.t('message.CONNECTION_ERROR'), [{
            title: i18n.t('message.OK'),
          }]);
        }
      });
  },

  /** Fetch a specific problem along with all related causes and actions */
  async [actionsTypes.SEND_REPORT](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/problems/sendReport.php',
      data: payload,
    });
  },

  /** Problem edition */
  async [actionsTypes.EDIT_PROBLEM](store, payload) {
    const axiosConfig = {
      method: 'put',
      url: `/server/problems/${payload.id}`,
      data: payload,
    };

    // ===================== Offline =====================
    // Problems can be created while offlineMode is on by storing the problems using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Edit the problem locally
        const problem = cloneDeep(store.state.problems[`${payload.id}`]);
        problem.setProps(payload);
        store.commit(mutationsTypes.SET_PROBLEM, problem);
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: payload });
        resolve(problem);
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          toastr.remove();
          toastr.success(i18n.t('message.PROBLEM_SAVED'));
          return data;
        }
        if (!payload.noMsgOnError) {
          if (data === -2) {
            calert(i18n.t('message.PB_NO_ACCESS'), [{ title: i18n.t('message.OK') }]);
          } else {
            calert(i18n.t('message.PB_EDITION_ERROR'), [{ title: i18n.t('message.OK') }]);
          }
        }
        throw data;
      })
      .catch(({ response }) => {
        if (response.status === 500) {
          calert(i18n.t('message.PB_EDITION_ERROR'), [{
            title: i18n.t('message.OK'),
          }]);
        } else {
          calert(i18n.t('message.PB_NO_ACCESS'), [{
            title: i18n.t('message.OK'),
          }]);
        }
      });
  },

  /** Cause creation */
  async [actionsTypes.ADD_CAUSE](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems/addCause.php',
      data: payload,
    };
    // ===================== Offline =====================
    // Causes can be created while offlineMode is on by storing the causes using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Get new id
        const id = Math.min(...Object.values(store.state.causes).map(pb => pb.id), 0) - 1;
        store.commit(mutationsTypes.SET_CAUSE, { ...payload, id, problem: payload.id });
        // Add the cause to its parent if any
        if (payload.cause) {
          const parentCause = cloneDeep(store.state.causes[`${payload.cause}`]);
          parentCause.setProps({
            wCauses: [
              ...parentCause.wCauses.map(childCause => cloneDeep(store.state.causes[`${childCause.id}`])),
              store.state.causes[`${id}`],
            ],
          });
          store.commit(mutationsTypes.SET_CAUSE, parentCause);
        }
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: { ...payload, causeId: id } });
        resolve({ ...payload, id, problem: payload.id });
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CAUSE, data);
          return data;
        }
        throw data;
      });
  },

  /** Cause edition */
  async [actionsTypes.EDIT_CAUSE](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems/editCause.php',
      data: payload,
    };
    // ===================== Offline =====================
    // Causes can be created while offlineMode is on by storing the causes using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Edit the problem locally
        const cause = cloneDeep(store.state.causes[`${payload.id}`]);
        cause.setProps(payload);
        store.commit(mutationsTypes.SET_CAUSE, cause);
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: payload });
        resolve(cause);
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_CAUSE, data);
          return data;
        }
        throw data;
      });
  },

  /** Cause deletion */
  async [actionsTypes.DELETE_CAUSE](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems/deleteCause.php',
      data: payload,
    };
    // ===================== Offline =====================
    // Causes can be created while offlineMode is on by storing the causes using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // If the cause has subcauses, attach them to the parent of the deleted cause
        const cause = cloneDeep(store.state.causes[`${payload.id}`]);
        cause.wCauses.forEach((subcause) => {
          const stateSubcause = cloneDeep(store.state.causes[`${subcause.id}`]);
          stateSubcause.setProps({ cause: cause.cause });
          store.commit(mutationsTypes.SET_CAUSE, stateSubcause);
        });
        store.commit(mutationsTypes.DELETE_CAUSE, cause);

        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: payload });
        resolve(1);
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.DELETE_CAUSE, { id: payload.id, problem: data.id });
          return data;
        }
        throw data;
      });
  },

  /** Fetch the history of a problem (also works for an action) */
  async [actionsTypes.FETCH_PROBLEM_HISTORY](store, payload) {
    store.commit(mutationsTypes.SET_HISTORY_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'get',
      url: `/server/problems/${payload.problemId}/history`,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM_HISTORY, { ...payload, data });
          return data;
        }
        throw data;
      });
  },

  /** Create a new action */
  async [actionsTypes.ADD_ACTION](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems/addAction.php',
      data: payload,
    };
    // ===================== Offline =====================
    // Actions can be created while offlineMode is on by storing the actions using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Get new id
        const id = Math.min(...Object.values(store.state.actions).map(a => a.id), 0) - 1;
        payload.problem = payload.problemId;
        const action = new Action({ ...payload, id, cause: payload.id }, store.rootState.currentUser);
        store.commit(mutationsTypes.SET_ACTION, action);
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: { ...payload, actionId: id } });
        resolve({ data: { ...payload, id } });
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then((response) => {
        const { data } = response;
        if (data instanceof Object && data.id) {
          store.commit(mutationsTypes.SET_ACTION, data);
          store.dispatch('FETCH_PENDING_ACTIONS_NUMBER', true, { root: true });
          return response;
        }
        throw response;
      });
  },

  /** Edit an action */
  async [actionsTypes.EDIT_ACTION](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/problems/editAction.php',
      data: payload,
    };
    // ===================== Offline =====================
    // Actions can be created while offlineMode is on by storing the actions using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Edit the action locally
        const newAction = payload.id === 0;
        payload.id = payload.id || Math.min(...Object.values(store.state.actions).map(a => a.id), 0) - 1;
        if (payload.problem > 0) {
          payload.problemId = payload.problem;
          const pb = store.state.problems[`${payload.problem}`];
          payload.problemTitle = pb ? pb.lWhat : '';
          payload.lWhat = payload.problemTitle;
        }
        let action = cloneDeep(store.state.actions[`${payload.id}`]) || {};
        action = new Action({ ...action, ...payload }, store.rootState.currentUser);
        store.commit(mutationsTypes.SET_ACTION, action);

        if (newAction) {
          // For new actions, use the addAction URL and specify the cause
          axiosConfig.url = '/server/problems/addAction.php';
          axiosConfig.data.actionId = payload.id;
          axiosConfig.data.id = payload.cause || 0;
        }
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios(axiosConfig);
        resolve({ data: action });
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then((response) => {
        const { data } = response;
        if (data instanceof Object && data.id) {
          store.commit(mutationsTypes.SET_ACTION, data);
          store.dispatch('FETCH_PENDING_ACTIONS_NUMBER', true, { root: true });
          return response;
        }
        throw response;
      });
  },

  /** Delete an action */
  async [actionsTypes.DELETE_ACTION](store, payload) {
    const axiosConfig = {
      method: 'post',
      url: '/server/actions/delete.php',
      data: payload,
    };
    // ===================== Offline =====================
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        const action = cloneDeep(store.state.actions[`${payload.id}`]);
        store.commit(mutationsTypes.DELETE_ACTION, action);

        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig, data: payload });
        resolve({ data: 1 });
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then((response) => {
        const { data } = response;
        if (data === 1) {
          store.commit(mutationsTypes.DELETE_ACTION, payload);
          store.dispatch('FETCH_PENDING_ACTIONS_NUMBER', true, { root: true });
          return response;
        }
        if (data === -2) {
          toastr.error(i18n.t('message.ACTION_RESPONSIBILITY_OTHER_TEAM'));
        }
        throw response;
      });
  },

  /** Delete a problem */
  async [actionsTypes.DELETE_PROBLEM](store, payload) {
    const axiosConfig = {
      method: 'delete',
      url: `/server/problems/${payload.id}`,
    };
    // ===================== Offline =====================
    // Problems can be created while offlineMode is on by storing the problems using a negative ID
    if (store.rootState.device.offlineMode === true) {
      return new Promise((resolve) => {
        // Edit the problem locally
        store.commit(mutationsTypes.DELETE_PROBLEM, payload);
        // Fake axios XHR for the network-manager to receive the request
        Vue.axios({ ...axiosConfig });
        resolve(1);
      });
    }

    // ===================== Online =====================
    return Vue.axios(axiosConfig)
      .then(({ data }) => {
        if (data === 1) {
          toastr.success(i18n.t('message.PROBLEM_DELETED'));
          store.commit(mutationsTypes.DELETE_PROBLEM, payload);
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PROBLEMS, { root: true });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      });
  },

  /** Archive a problem */
  async [actionsTypes.ARCHIVE_PROBLEM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/problems/archive.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          toastr.success(i18n.t('message.PROBLEM_ARCHIVED'));
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PROBLEMS, { root: true });
          store.dispatch(`socket/${SEND_MESSAGE}`, {
            msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
            id: payload.id,
            socketCompany: 1,
          }, { root: true });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      });
  },

  /** Restore an archived problem */
  async [actionsTypes.RESTORE_PROBLEM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/problems/restore.php',
      data: payload,
    })
      .then(({ data }) => {
        if (data instanceof Object) {
          store.commit(mutationsTypes.SET_PROBLEM, data);
          toastr.success(i18n.t('message.PROBLEM_RESTORED'));
          store.dispatch(`socket/${SEND_MESSAGE}`, SocketMessages.CC_MSGS.PROBLEMS, { root: true });
          store.dispatch(`socket/${SEND_MESSAGE}`, {
            msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
            id: payload.id,
            socketCompany: 1,
          }, { root: true });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      });
  },

};
